import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box, Divider, IconButton, useMediaQuery, useTheme, Tooltip,
  Badge,
} from "@mui/material";

import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";

import { useNavigate } from "react-router-dom";

// Import Logo
import AskGiiaLogo from "../Assets/askgiia3.png";
import home4 from "../Assets/home4.png";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  error: "#DC2626",
};

const BASE_URL = "https://backend-global-1vq9.onrender.com/api";

// ── Count fetcher ────────────────────────────────────────────────────────────
// Counts only "actionable" notifications: pending requests (mentor) or recently
// resolved requests (learner), plus sessions starting in the next 24h.
const fetchActionableCount = async (user) => {
  if (!user) return 0;
  const userId = user.id || user._id;
  const role = (user.role || "").toLowerCase();
  const isMentor = role === "mentor";

  let count = 0;

  // Connection requests
  try {
    const path = isMentor ? "mentor" : "learner";
    const res = await axios.get(`${BASE_URL}/connection-requests/${path}/${userId}`);
    const requests = res.data?.data || [];

    if (isMentor) {
      // Mentor: count pending (needs their action)
      count += requests.filter((r) => r.status === "pending").length;
    } else {
      // Learner: count resolved-in-last-24h (something to see)
      const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
      count += requests.filter((r) =>
        (r.status === "accepted" || r.status === "rejected") &&
        new Date(r.updatedAt || r.createdAt).getTime() > dayAgo
      ).length;
    }
  } catch {
    // Fail quietly — the badge just won't include this source
  }

  // Sessions starting within the next 24h
  try {
    const url = isMentor
      ? `${BASE_URL}/sessions?mentorId=${userId}`
      : `${BASE_URL}/sessions`;
    const res = await axios.get(url);
    const sessions = res.data?.data || res.data || [];
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;

    count += sessions.filter((s) => {
      if (!s.date) return false;
      const time = s.startTime || "00:00";
      const startMs = new Date(`${s.date}T${time}`).getTime();
      return !isNaN(startMs) && startMs > now && (startMs - now) < dayMs;
    }).length;
  } catch {
    // Fail quietly
  }

  return count;
};

export default function TopBar({ onMenuClick }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Tooltip auto-show for Ask Giia (existing behaviour)
  const [forceOpen, setForceOpen] = useState(false);
  const [hovering, setHovering] = useState(false);

  // Notification badge state
  const [notifCount, setNotifCount] = useState(0);

  // Current user from localStorage (read once at mount; re-reads on login changes)
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("user")); }
    catch { return null; }
  });

  useEffect(() => {
    const alreadyShown = sessionStorage.getItem("askGiiaHintShown");
    if (!alreadyShown) {
      setForceOpen(true);
      sessionStorage.setItem("askGiiaHintShown", "true");
      const timer = setTimeout(() => setForceOpen(false), 4000);
      return () => clearTimeout(timer);
    }
  }, []);

  // ── Poll notification count ──
  const refreshCount = useCallback(async () => {
    if (!user) return;
    const c = await fetchActionableCount(user);
    setNotifCount(c);
  }, [user]);

  useEffect(() => {
    refreshCount();
    const interval = setInterval(refreshCount, 60_000); // every minute
    return () => clearInterval(interval);
  }, [refreshCount]);

  const handleAskGiiaClick = () => {
    setForceOpen(false);
    navigate("/giaAgent");
  };

  const handleNotificationClick = () => {
    navigate("/notification");
  };

  const tooltipOpen = forceOpen || hovering;

  return (
    <Box
      sx={{
        // Fixed responsive width — accounts for 234px sidebar on desktop
        width: { xs: "100%", md: "calc(100% - 0px)" },
        ml: { xs: 0, md: "00px" },

        // ── Mobile clearance ────────────────────────────────────────
        // SideNavbar renders a fixed 64px top bar on mobile (with the
        // hamburger menu). Push our content down so it isn't covered.
        // The mobile bar uses paddingTop/paddingBottom of 5 (each ≈40px)
        // plus 64px declared height, so the real visible bottom is ~120px.
        mt: { xs: "120px", md: 0 },

        // ── Sticky / fixed behaviour ────────────────────────────────
        // Stays pinned to the top of the page as you scroll. `top` matches
        // `mt` exactly so the bar doesn't jump when it switches from its
        // initial flow position to "stuck" — on mobile it sticks just
        // below the SideNavbar's own fixed bar instead of under/behind it.
        position: "sticky",
        top: { xs: "120px", md: 0 },
        zIndex: theme.zIndex.appBar,

        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        bgcolor: C.paper,
        py: { xs: 1.5, sm: 2 },
        px: { xs: 2, sm: 3, md: 4 },
        mb: 3,
        borderBottom: `1px solid ${C.border}`,
        gap: 2,
        overflow: "visible",
        flexWrap: { xs: "wrap", md: "nowrap" },
      }}
    >
      {/* ── LEFT SECTION ─────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, sm: 2 },
          flex: 1,
          minWidth: 0,
          width: { xs: "50%", md: "auto" },
        }}
      >
        {/* Home logo — replaces the greeting bubble */}
        <Box
          component="img"
          src={home4}
          alt="Home"
          sx={{
            display: "block",
            width: { xs: 60, sm: 80, md: 90 },
            height: "auto",
            objectFit: "contain",
            flexShrink: 0,
          }}
        />
      </Box>

      {/* ── RIGHT SECTION ────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "space-between", md: "flex-end" },
          width: { xs: "100%", md: "auto" },
          flexShrink: 0,
          gap: { xs: 1, sm: 2, md: 3 },
        }}
      >
        {/* ASK GIIA LOGO + TOOLTIP */}
        <Tooltip
          title="Ask Giia — your AI assistant 🤖"
          arrow
          placement="bottom"
          open={tooltipOpen}
          onOpen={() => setHovering(true)}
          onClose={() => setHovering(false)}
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: C.primary,
                fontSize: 13,
                fontWeight: 600,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
              },
            },
            arrow: { sx: { color: C.primary } },
          }}
        >
          <Box
            onClick={handleAskGiiaClick}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
              flexShrink: 0,
              "&:hover": { transform: "translateY(-1px)" },
            }}
          >
            <Box
              component="img"
              src={AskGiiaLogo}
              alt="Ask Giia"
              sx={{
                display: "block",
                width: { xs: 85, sm: 95, md: 120, lg: 135 },
                height: "auto",
                objectFit: "contain",
                flexShrink: 0,
              }}
            />
          </Box>
        </Tooltip>

        {/* DIVIDER */}
        {!isMobile && (
          <Divider
            orientation="vertical"
            flexItem
            sx={{ height: 42, bgcolor: C.border }}
          />
        )}

        {/* ── NOTIFICATIONS ────────────────────────────────────── */}
        <Tooltip
          title={
            notifCount > 0
              ? `${notifCount} new notification${notifCount !== 1 ? "s" : ""}`
              : "Notifications"
          }
          arrow
          placement="bottom"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: C.primary,
                fontSize: 13,
                fontWeight: 600,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
              },
            },
            arrow: { sx: { color: C.primary } },
          }}
        >
          <IconButton
            onClick={handleNotificationClick}
            sx={{
              position: "relative",
              p: 1.25,
              borderRadius: 2,
              bgcolor: notifCount > 0 ? C.primarySoft : "transparent",
              color: notifCount > 0 ? C.primary : C.textSecondary,
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: C.primarySoft,
                color: C.primary,
                transform: "translateY(-1px)",
              },
            }}
          >
            <Badge
              // Cap at 99+, hide when zero
              badgeContent={notifCount > 99 ? "99+" : notifCount}
              invisible={notifCount === 0}
              overlap="circular"
              sx={{
                "& .MuiBadge-badge": {
                  bgcolor: C.error,
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 10,
                  height: 18,
                  minWidth: 18,
                  borderRadius: 9,
                  px: 0.5,
                  border: `2px solid ${C.paper}`,
                  boxShadow: `0 2px 6px ${C.error}40`,
                  // Pulse animation to draw attention when there are unread items
                  animation: notifCount > 0 ? "notifPulse 2s ease-in-out infinite" : "none",
                  "@keyframes notifPulse": {
                    "0%, 100%": {
                      transform: "scale(1)",
                      boxShadow: `0 2px 6px ${C.error}40`,
                    },
                    "50%": {
                      transform: "scale(1.08)",
                      boxShadow: `0 2px 10px ${C.error}70`,
                    },
                  },
                },
              }}
            >
              <NotificationsNoneIcon
                sx={{
                  fontSize: { xs: 22, sm: 24 },
                  color: "inherit",
                }}
              />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}